import {captureMessage} from '@sentry/react';

import {equals, isNil} from 'ramda';

import {getLocalFinancialProductQuote} from 'features/financial-products';

import {FinancialProductOffer, PublicFinancialProductsResponseBody} from './types/onlineOfferTypes';

const QUOTE_MISMATCH_FINGERPRINT = 'online-offer-financial-product-quote-mismatch';
const QUOTE_MISMATCH_MESSAGE = 'Local financial product quote differs from the stored quote';

// One report per offer and coordinates for the lifetime of the page; every new body repeats the
// check.
const reportedMismatches = new Set<string>();

const reportOfferMismatch = (
  offerId: string,
  products: PublicFinancialProductsResponseBody,
  offer: FinancialProductOffer | null
) => {
  const stored = offer?.calculation;

  if (isNil(offer) || isNil(stored)) {
    return;
  }

  const coordinates = {
    productType: offer.productType,
    termMonths: stored.termMonths,
    downPaymentPercentage: stored.downPaymentPercentage,
    balloonPaymentPercentage: stored.balloonPaymentPercentage,
    leasingTargetGroup: stored.leasingTargetGroup,
    annualMileage: stored.annualMileage,
  };
  const local = getLocalFinancialProductQuote(products, offer, coordinates);
  const key = JSON.stringify([offerId, coordinates]);

  if (isNil(local) || equals(local.calculation, stored) || reportedMismatches.has(key)) {
    return;
  }

  reportedMismatches.add(key);
  // Quote members and coordinates only: the public payload names no customer or seller.
  captureMessage(QUOTE_MISMATCH_MESSAGE, {
    level: 'warning',
    fingerprint: [QUOTE_MISMATCH_FINGERPRINT, offer.productType],
    tags: {financialProductType: offer.productType},
    extra: {
      offerId,
      coordinates,
      stored,
      local: local.calculation,
      localUnavailableReason: local.unavailableReason,
    },
  });
};

/**
 * Tells Sentry when the backend's stored quote and the local quote for the same coordinates and
 * the same published inputs differ, the production signal that the calculators drifted apart.
 * Withheld rates (an older backend) or a contract snapshot have no local quote to compare.
 * Called for every received read and write body; a 409's `current` body is checked by the next poll.
 */
export const reportFinancialProductQuoteMismatch = (
  offerId: string,
  products: PublicFinancialProductsResponseBody
) => {
  [products.standard, products.balloon, products.leasing].forEach((offer) =>
    reportOfferMismatch(offerId, products, offer)
  );
};
